import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '' // Empty string for production (relative URL)
  : 'http://localhost:5001';

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { token: paramToken } = useParams();
  const token = paramToken || searchParams.get('token');
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(null);
  const [isTokenChecking, setIsTokenChecking] = useState(true);
  
  // Verify the token is valid when component mounts
  useEffect(() => {
    if (!token) {
      setIsTokenValid(false);
      setIsTokenChecking(false);
      setError(t('resetPassword.missingToken', 'Reset token is missing. Please check your email link.'));
      return;
    }
    
    const verifyToken = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/check-reset-token/${token}`);
        const data = await response.json();
        
        setIsTokenValid(response.ok);
        
        if (!response.ok) {
          setError(data.error || t('resetPassword.invalidToken', 'Invalid or expired reset token'));
        }
      } catch (err) {
        setIsTokenValid(false);
        setError(t('resetPassword.serverError', 'Server error. Please try again later.'));
      } finally {
        setIsTokenChecking(false);
      }
    };
    
    verifyToken();
  }, [token, t]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (password.length < 12) {
      setError(t('resetPassword.passwordTooShort', 'Password must be at least 12 characters long'));
      return;
    }
    
    if (password !== confirmPassword) {
      setError(t('resetPassword.passwordMismatch', 'Passwords do not match'));
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          password: encodeURIComponent(password)
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || t('resetPassword.error', 'Failed to reset password'));
      }
      
      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { 
          state: { message: t('resetPassword.successMessage', 'Your password has been reset. You can now log in with your new password.') }
        });
      }, 3000);
      
    } catch (err) {
      setError(err.message || t('resetPassword.serverError', 'Server error. Please try again later.'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const renderContent = () => {
    if (isTokenChecking) {
      return (
        <div className="text-center py-4">
          <div className="animate-spin inline-block w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full mb-2"></div>
          <p>{t('resetPassword.verifyingToken', 'Verifying your reset link...')}</p>
        </div>
      );
    }
    
    if (!isTokenValid) {
      return (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
          <p className="font-medium">{t('resetPassword.invalidTokenTitle', 'Invalid Reset Link')}</p>
          <p>{error}</p>
          <p className="mt-4">
            <Link to="/forgot-password" className="text-blue-600 hover:underline">
              {t('resetPassword.requestNewLink', 'Request a new reset link')}
            </Link>
          </p>
        </div>
      );
    }
    
    if (success) {
      return (
        <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
          <p className="font-medium">{t('resetPassword.successTitle', 'Password Reset Complete!')}</p>
          <p>{t('resetPassword.successDetail', 'Your password has been successfully reset.')}</p>
          <p>{t('resetPassword.redirecting', 'Redirecting to login page...')}</p>
        </div>
      );
    }
    
    return (
      <>
        <p className="mb-6 text-center text-gray-600">
          {t('resetPassword.instruction', 'Enter your new password below')}
        </p>
        
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">
              {t('resetPassword.newPassword', 'New Password')}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={isSubmitting}
            />
            <p className="text-sm text-gray-500 mt-1">
              {t('resetPassword.passwordRequirement', 'Must be at least 12 characters long')}
            </p>
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="confirmPassword">
              {t('resetPassword.confirmPassword', 'Confirm New Password')}
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={isSubmitting}
            />
          </div>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded focus:outline-none transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
          >
            {isSubmitting 
              ? t('resetPassword.resetting', 'Resetting...') 
              : t('resetPassword.submit', 'Reset Password')}
          </button>
        </form>
      </>
    );
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-md mx-auto pt-16 px-4">
        <div className="bg-white p-8 border rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-6 text-center">
            {t('resetPassword.title', 'Reset Your Password')}
          </h2>
          
          {renderContent()}
          
          {!success && (
            <div className="mt-6 text-center">
              <Link to="/login" className="text-blue-600 hover:underline">
                {t('resetPassword.backToLogin', 'Back to Login')}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage; 